import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

const SECTIONS = {
  home: ['hero_title', 'hero_subtitle', 'gpus_title', 'gpus_subtitle', 'services_title', 'services_subtitle', 'trust_iso', 'trust_gdpr', 'trust_ai_act', 'trust_location', 'cta_title', 'cta_text', 'cta_button'],
  services: ['page_title', 'page_subtitle'],
  infrastructure: ['page_title', 'page_subtitle'],
  about: ['page_title', 'page_subtitle', 'overview_title'],
  contact: ['page_title', 'page_subtitle', 'form_title', 'form_subtitle', 'contact_info_title']
};

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};

function AdminDashboard() {
  const [pages] = useState(['home', 'services', 'infrastructure', 'about', 'contact', 'analytics']);
  const [selectedPage, setSelectedPage] = useState('home');
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const navigate = useNavigate();

  const fetchOpts = { credentials: 'include' };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (selectedPage && selectedPage !== 'analytics') {
      fetchContent(selectedPage);
    } else if (selectedPage === 'analytics') {
      fetchAnalytics();
    }
  }, [selectedPage]);

  const checkAuth = async () => {
    const res = await fetch('/api/admin/check', fetchOpts);
    const data = await res.json();
    if (!data.authenticated) {
      navigate('/admin');
    }
  };

  const fetchContent = async (page) => {
    const res = await fetch(`/api/admin/content?page=${page}`, fetchOpts);
    const data = await res.json();
    setContent(data);
  };

  const fetchAnalytics = async () => {
    const res = await fetch('/api/admin/analytics?days=30', fetchOpts);
    const data = await res.json();
    setAnalytics(data);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    for (const [section, value] of Object.entries(content)) {
      await fetch('/api/admin/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: selectedPage, section, content: value }),
        ...fetchOpts
      });
    }
    setSaving(false);
    setMessage('Saved successfully!');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleLogout = async () => {
    await fetch('/api/admin/logout', { method: 'POST', ...fetchOpts });
    navigate('/admin');
  };

  const renderAnalytics = () => {
    if (!analytics) return <div>Loading analytics...</div>;
    
    return (
      <div className="analytics-grid">
        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="admin-stat-card">
              <Card.Body className="text-center">
                <h3 className="admin-stat-number">{analytics.today_views || 0}</h3>
                <p className="admin-stat-label">Today's Views</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="admin-stat-card">
              <Card.Body className="text-center">
                <h3 className="admin-stat-number">{analytics.total_views || 0}</h3>
                <p className="admin-stat-label">Total Views (30 days)</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="admin-stat-card">
              <Card.Body className="text-center">
                <h3 className="admin-stat-number">{analytics.page_views?.length || 0}</h3>
                <p className="admin-stat-label">Pages Tracked</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <Row className="g-3 mb-4">
          <Col md={6}>
            <Card className="admin-table-card">
              <Card.Header>Top Pages</Card.Header>
              <Card.Body>
                {analytics.page_views && analytics.page_views.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th className="text-end">Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.page_views.map((p, i) => (
                        <tr key={i}>
                          <td>{p.page || 'Home'}</td>
                          <td className="text-end">{p.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="admin-no-data">No data yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="admin-table-card">
              <Card.Header>Daily Views (Last 7 Days)</Card.Header>
              <Card.Body>
                {analytics.daily_views && analytics.daily_views.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-end">Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.daily_views.slice(-7).map((d, i) => (
                        <tr key={i}>
                          <td>{d.date}</td>
                          <td className="text-end">{d.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="admin-no-data">No data yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <Card className="admin-table-card">
          <Card.Header>Hourly Distribution</Card.Header>
          <Card.Body>
            {analytics.hourly_distribution && analytics.hourly_distribution.length > 0 ? (
              <div className="hourly-grid">
                {analytics.hourly_distribution.map((h, i) => (
                  <div key={i} className="hourly-item">
                    <div className="hourly-bar" style={{ 
                      height: `${Math.max(5, (h.views / Math.max(...analytics.hourly_distribution.map(x => x.views))) * 100)}%` 
                    }} />
                    <small className="hourly-label">{h.hour}:00</small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-no-data">No data yet</p>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderContentEditor = () => {
    const sections = SECTIONS[selectedPage] || [];
    return (
      <Card.Body className="admin-editor-body">
        {sections.map(section => (
          <div key={section} className="mb-4">
            <label className="form-label text-capitalize">{section.replace(/_/g, ' ')}</label>
            <ReactQuill
              theme="snow"
              value={content[section] || ''}
              onChange={(value) => setContent({ ...content, [section]: value })}
              modules={quillModules}
              className="admin-quill"
            />
          </div>
        ))}
      </Card.Body>
    );
  };

  return (
    <>
      <div className="admin-dashboard">
        <nav className="admin-navbar">
          <Container fluid>
            <span className="admin-nav-brand">6G AI Admin</span>
            <div className="admin-nav-actions">
              <Button variant="outline-light" size="sm" as={Link} to="/" target="_blank">View Site</Button>
              <Button variant="outline-danger" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </Container>
        </nav>
        
        <Container className="py-4">
          <Row>
            <Col md={3}>
              <div className="admin-sidebar">
                {pages.map(page => (
                  <button
                    key={page}
                    className={`admin-sidebar-item ${selectedPage === page ? 'active' : ''}`}
                    onClick={() => setSelectedPage(page)}
                  >
                    {page === 'analytics' ? '📊 Analytics' : page.charAt(0).toUpperCase() + page.slice(1)}
                  </button>
                ))}
              </div>
            </Col>
            
            <Col md={9}>
              {selectedPage === 'analytics' ? (
                <Card className="admin-content-card">
                  <Card.Header>Website Analytics</Card.Header>
                  {renderAnalytics()}
                </Card>
              ) : (
                <Card className="admin-content-card">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Edit: {selectedPage}</h5>
                    <div>
                      {message && <span className="admin-success-message me-3">{message}</span>}
                      <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </Card.Header>
                  {renderContentEditor()}
                </Card>
              )}
              
              <div className="mt-4">
                <Link to="/" className="admin-back-link">← Back to Website</Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      
      <style>{`
        .admin-dashboard {
          min-height: 100vh;
          background: var(--md-sys-color-surface);
        }
        
        .admin-navbar {
          background: var(--md-sys-color-surface-dim);
          border-bottom: 1px solid var(--md-sys-color-outline-variant);
          padding: var(--space-md) 0;
        }
        
        .admin-nav-brand {
          color: var(--md-sys-color-primary);
          font-weight: 700;
          font-size: var(--font-title-lg);
        }
        
        .admin-nav-actions {
          display: flex;
          gap: var(--space-sm);
        }
        
        .admin-sidebar {
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }
        
        .admin-sidebar-item {
          background: var(--md-sys-color-surface-container);
          border: 1px solid var(--md-sys-color-outline-variant);
          color: var(--md-sys-color-on-surface);
          padding: var(--space-md);
          border-radius: var(--radius-medium);
          cursor: pointer;
          transition: all var(--motion-duration-short2) var(--motion-easing-standard);
          text-align: left;
          font-weight: 500;
        }
        
        .admin-sidebar-item:hover {
          background: var(--md-sys-color-surface-container-high);
        }
        
        .admin-sidebar-item.active {
          background: var(--md-sys-color-primary-container);
          color: var(--md-sys-color-on-primary-container);
          border-color: var(--md-sys-color-primary);
        }
        
        .admin-content-card {
          background: var(--md-sys-color-surface-container);
          border: 1px solid var(--md-sys-color-outline-variant);
          border-radius: var(--radius-extra-large);
        }
        
        .admin-content-card .card-header {
          background: var(--md-sys-color-surface-container-high);
          border-bottom: 1px solid var(--md-sys-color-outline-variant);
          color: var(--md-sys-color-on-surface);
          font-weight: 600;
        }
        
        .admin-stat-card {
          background: var(--md-sys-color-surface-container);
          border: 1px solid var(--md-sys-color-outline-variant);
          border-radius: var(--radius-large);
        }
        
        .admin-stat-number {
          color: var(--md-sys-color-primary);
          font-weight: 700;
          font-size: var(--font-headline-lg);
          margin-bottom: var(--space-xs);
        }
        
        .admin-stat-label {
          color: var(--md-sys-color-on-surface-variant);
          font-size: var(--font-body-sm);
          margin-bottom: 0;
        }
        
        .admin-table-card {
          background: var(--md-sys-color-surface-container);
          border: 1px solid var(--md-sys-color-outline-variant);
          border-radius: var(--radius-large);
        }
        
        .admin-table-card .card-header {
          background: var(--md-sys-color-surface-container-high);
          border-bottom: 1px solid var(--md-sys-color-outline-variant);
          color: var(--md-sys-color-on-surface);
          font-weight: 600;
        }
        
        .admin-table {
          width: 100%;
          color: var(--md-sys-color-on-surface);
        }
        
        .admin-table th,
        .admin-table td {
          padding: var(--space-sm);
          border-bottom: 1px solid var(--md-sys-color-outline-variant);
        }
        
        .admin-table th {
          font-weight: 600;
          color: var(--md-sys-color-on-surface-variant);
        }
        
        .admin-no-data {
          color: var(--md-sys-color-on-surface-variant);
          font-style: italic;
        }
        
        .hourly-grid {
          display: flex;
          align-items: flex-end;
          gap: var(--space-xs);
          height: 150px;
          padding: var(--space-md) 0;
        }
        
        .hourly-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }
        
        .hourly-bar {
          width: 100%;
          background: var(--md-sys-color-primary);
          border-radius: var(--radius-small) var(--radius-small) 0 0;
          transition: height var(--motion-duration-medium2) var(--motion-easing-emphasized);
        }
        
        .hourly-label {
          color: var(--md-sys-color-on-surface-variant);
          font-size: var(--font-label-sm);
          margin-top: var(--space-xs);
        }
        
        .admin-editor-body {
          background: var(--md-sys-color-surface-container);
        }
        
        .admin-quill {
          background: var(--md-sys-color-surface-dim);
          border-radius: var(--radius-medium);
        }
        
        .admin-success-message {
          color: var(--md-sys-color-tertiary);
          font-weight: 500;
        }
        
        .admin-back-link {
          color: var(--md-sys-color-on-surface-variant);
          transition: color var(--motion-duration-short2) var(--motion-easing-standard);
        }
        
        .admin-back-link:hover {
          color: var(--md-sys-color-primary);
        }
      `}</style>
    </>
  );
}

export default AdminDashboard;
